import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { jobService } from "../services/job.service";
import { Button, Icon, Input, StatusPill } from "../ds";
import { Page, PageHeader, Section, Loading, Note } from "../components/page";

// One block of the generated JD: a list, one point per line.
const PointList = ({ items }) => (
  <ul style={{ margin: 0, paddingLeft: 20, display: 'grid', gap: 8, color: 'var(--text-2)' }}>
    {items.map((item, index) => <li key={index}>{item}</li>)}
  </ul>
);
const STORAGE_KEY = 'jobDescription';

const GeneratedJobDescription = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generatedData, setGeneratedData] = useState(null);
  const [inputContent, setInputContent] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const generationAttempted = useRef(false);
  const [generatingInitial, setGeneratingInitial] = useState(false);

  useEffect(() => {
    let mounted = true;

    const generateDescription = async () => {
      if (generationAttempted.current) return;
      generationAttempted.current = true;

      try {
        if (!location.state) {
          navigate("/jobs/create");
          return;
        }

        setGeneratingInitial(true);
        setLoading(true);

        const { jobTitle, department, qualifications, responsibilities } = location.state;
        
        const jobResponse = await jobService.createJob({
          jobTitle,
          department,
          qualifications,
          responsibilities
        });

        if (!mounted) return;

        if (jobResponse.data) {
          const jobData = jobResponse.data;
          
          // Handle both create API and continue API response structures
          let generatedContent = {
            overview: "No overview available",
            qualifications: ["No qualifications generated"],
            responsibilities: ["No responsibilities generated"],
            requirements: ["No requirements generated"]
          };

          // Check if we have jobDescription from create API
          if (jobData.jobDescription) {
            generatedContent = {
              overview: jobData.jobDescription.overview || "No overview available",
              qualifications: Array.isArray(jobData.jobDescription.generatedQualifications) 
                ? jobData.jobDescription.generatedQualifications 
                : jobData.jobDescription.generatedQualifications 
                  ? [jobData.jobDescription.generatedQualifications]
                  : ["No qualifications generated"],
              responsibilities: Array.isArray(jobData.jobDescription.generatedResponsibilities) 
                ? jobData.jobDescription.generatedResponsibilities 
                : jobData.jobDescription.generatedResponsibilities 
                  ? [jobData.jobDescription.generatedResponsibilities]
                  : ["No responsibilities generated"],
              requirements: Array.isArray(jobData.jobDescription.requirements) 
                ? jobData.jobDescription.requirements 
                : jobData.jobDescription.requirements 
                  ? [jobData.jobDescription.requirements]
                  : ["No requirements generated"]
            };
          }
          // Check if we have content from continue API structure
          else if (jobData.content?.content) {
            const content = jobData.content.content;
            generatedContent = {
              overview: content.overview || "No overview available",
              qualifications: Array.isArray(content.qualifications) 
                ? content.qualifications 
                : content.qualifications 
                  ? [content.qualifications]
                  : ["No qualifications generated"],
              responsibilities: Array.isArray(content.responsibilities) 
                ? content.responsibilities 
                : content.responsibilities 
                  ? [content.responsibilities]
                  : ["No responsibilities generated"],
              requirements: Array.isArray(content.requirements) 
                ? content.requirements 
                : content.requirements 
                  ? [content.requirements]
                  : ["No requirements generated"]
            };
          }

          const formattedData = {
            jobTitle: jobData.title,
            department: jobData.department,
            qualifications: jobData.qualifications,
            responsibilities: jobData.responsibilities,
            _id: jobData._id,
            jobId: jobData.jobId,
            threadId: jobData.threadId,
            assistantId: jobData.assistantId,
            createdBy: jobData.createdBy,
            generatedContent: generatedContent
          };

          // Store in localStorage
          localStorage.setItem(`${STORAGE_KEY}_${jobData._id}`, JSON.stringify(formattedData));
          setGeneratedData(formattedData);
        } else {
          throw new Error('Job description not generated');
        }
      } catch (err) {
        if (mounted) {
          console.error("Error generating description:", err);
          setError(err.response?.data?.message || "Failed to generate job description");
        }
      } finally {
        if (mounted) {
          setGeneratingInitial(false);
          setLoading(false);
        }
      }
    };

    generateDescription();

    return () => {
      mounted = false;
    };
  }, [location.state, navigate]);

  // Update localStorage when content changes
  useEffect(() => {
    if (generatedContent && generatedData?._id) {
      const updatedData = {
        ...generatedData,
        generatedContent: generatedContent
      };
      localStorage.setItem(`${STORAGE_KEY}_${generatedData._id}`, JSON.stringify(updatedData));
    }
  }, [generatedContent, generatedData]);

  const handleGenerateContent = async () => {
    if (!inputContent.trim()) return;
    setIsGenerating(true);
  
    try {
      // Prepare existing content for continuation
      const existingContent = generatedContent ? {
        overview: generatedContent.overview,
        qualifications: generatedContent.qualifications,
        responsibilities: generatedContent.responsibilities,
        requirements: generatedContent.requirements
      } : null;

      const response = await jobService.continueJobDescription(generatedData._id, {
        user_message: inputContent.trim(), // Only send if not empty
        existing_content: existingContent
      });

      console.log('Continue response in component:', response);
      console.log('Response.content:', response.content);
      console.log('Response.content.content:', response.content?.content);

      // Handle nested content structure - API returns response.content.content
      const content = response.content?.content || response.content || response.data?.content;
      
      console.log('Extracted content:', content);
      
      if (content) {
        // Ensure we have arrays for all fields and provide fallbacks
        const processedContent = {
          overview: content.overview || generatedData.generatedContent?.overview || "No overview available",
          qualifications: Array.isArray(content.qualifications) 
            ? content.qualifications 
            : content.qualifications 
              ? [content.qualifications]
              : generatedData.generatedContent?.qualifications || ["No qualifications generated"],
          responsibilities: Array.isArray(content.responsibilities) 
            ? content.responsibilities 
            : content.responsibilities 
              ? [content.responsibilities]
              : generatedData.generatedContent?.responsibilities || ["No responsibilities generated"],
          requirements: Array.isArray(content.requirements) 
            ? content.requirements 
            : content.requirements 
              ? [content.requirements]
              : generatedData.generatedContent?.requirements || ["No requirements generated"]
        };
        console.log('Processed content:', processedContent);
        setGeneratedContent(processedContent);
        setInputContent(''); // Clear input after successful generation
        
        // Update localStorage with new content
        const updatedData = {
          ...generatedData,
          generatedContent: processedContent
        };
        localStorage.setItem(`${STORAGE_KEY}_${generatedData._id}`, JSON.stringify(updatedData));
      } else {
        console.error('No content found in response:', response);
        throw new Error("Received invalid response format from the service");
      }
    } catch (err) {
      console.error("Error generating new content:", err);
      setError(err.message || "Failed to generate new content");
    } finally {
      setIsGenerating(false);
    }
  };
  
  const handleSaveJob = async () => {
    try {
      if (!generatedContent) {
        navigate(`/jobs/${generatedData._id}?new=1`); // offers the existing CVs (F11)
        return;
      }

      const response = await jobService.updateJobDescription(generatedData._id, {
        overview: generatedContent.overview,
        qualifications: generatedContent.qualifications,
        responsibilities: generatedContent.responsibilities,
        requirements: generatedContent.requirements
      });
      
      console.log("Save job response:", response);
      if (response.success) {
        navigate(`/jobs/${generatedData._id}?new=1`); // offers the existing CVs (F11)
      }
    } catch (err) {
      console.error("Error saving job:", err);
      setError("Failed to save job. Please try again.");
    }
  };


  // The ask-for-a-change box (was the shared PromptInput, drawn here with the
  // design system; same rules: sends only a non-empty prompt, not while busy).
  const handlePromptSubmit = (e) => {
    e.preventDefault();
    if (!isGenerating && inputContent.trim()) {
      handleGenerateContent();
    }
  };

  if (error) {
    return (
      <Page>
        <PageHeader title="Job description" />
        <Note tone="danger">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ flex: 1, minWidth: 200 }}>{error}</span>
            <Button size="sm" variant="secondary" onClick={() => navigate("/jobs/create")}>
              Go Back
            </Button>
          </div>
        </Note>
      </Page>
    );
  }

  if (generatingInitial) {
    return <Loading label="Generating Job Description" />;
  }

  if (!generatedData) {
    return <Loading />;
  }

  return (
    <Page>
      <PageHeader
        title="Review the job description"
        subtitle="Read what the hiring assistant wrote, ask it for changes, then save."
        actions={(
          <>
            <StatusPill tone="info">{generatedData.jobTitle}</StatusPill>
            {/* <StatusPill>Full time</StatusPill> */}
            <StatusPill tone="neutral">{generatedData.department}</StatusPill>
          </>
        )}
      />

      <Section title="Overview">
        <p style={{ margin: 0, color: 'var(--text-2)' }}>
          {generatedContent?.overview ||
            generatedData.generatedContent.overview}
        </p>
      </Section>

      <Section title="Key Responsibilities">
        <PointList items={generatedContent?.responsibilities || generatedData.generatedContent.responsibilities} />
      </Section>

      <Section title="Qualifications">
        <PointList items={generatedContent?.qualifications || generatedData.generatedContent.qualifications} />
      </Section>

      <Section title="Requirements">
        <PointList items={generatedContent?.requirements || generatedData.generatedContent.requirements} />
      </Section>

      <Section title="Ask the assistant" subtitle="Describe a change and it rewrites the description.">
        <form onSubmit={handlePromptSubmit} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <Input
              id="jd-prompt"
              placeholder="Enter your prompt..."
              value={inputContent}
              onChange={(e) => setInputContent(e.target.value)}
              disabled={isGenerating}
            />
          </div>
          <Button
            type="submit"
            variant="secondary"
            loading={isGenerating}
            disabled={!inputContent.trim()}
            icon={<Icon name="paper-plane-right" />}
            aria-label="Send"
          >
            Send
          </Button>
          {/* As before, Save is the form's other submit button. */}
          <Button type="submit" onClick={handleSaveJob} disabled={isGenerating}>
            Save & Continue
          </Button>
        </form>
      </Section>
    </Page>
  );
};

export default GeneratedJobDescription;
